import { useState } from 'react';
import ShareSource from '@alltrails/analytics/enums/ShareSource';
import { defineMessages } from '@alltrails/shared/react-intl';
import useActivityStrings from '@alltrails/shared/hooks/useActivityStrings';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { Context } from '@alltrails/shared/types/context';
import { Trail } from 'types/Trails';
import PortalModal from 'components/shared/PortalModal';
import ActivityUploadModal from '@alltrails/modules/ActivityUpload/ActivityUploadModal';
import { modalRoadblock } from '@alltrails/shared/utils/modalFunnelUtils';
import { getEditTrackParams, EditTrackResponse } from '@alltrails/modules/ActivityUpload/requests';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import logError from '../../../utils/logError';
import ActionHeader from '../../shared/ActionHeader';
import DeleteModal from '../../DeleteRecording/DeleteModal/DeleteModal';
import DownloadRouteModal from '../../DownloadRouteModal';

type Props = {
  forcePhotoId?: string;
  track?: {
    type: string;
    slug: string;
  };
  context?: Context;
  data?: any;
  blockNonUserCreation?: (ref: string) => void;
  handleShareClick?: (something: unknown) => void;
  messagingChannel?: {
    publish: (
      event: string,
      track: {
        editingEndpoint: boolean;
        canceled?: boolean;
      }
    ) => void;
  };
  isFavorite?: boolean;
  handleFavoriteClick?: () => void;
  saveMapAsClone?: (data: any) => void;
  handleTrackUpdate?: (payload: any) => void;
  height?: number;
  isMobileWidth?: boolean;
  linkable?: boolean;
  showStaticMap?: boolean;
  tabletBrowser?: boolean;
  width?: number;
  handleRatingChange?: (e: React.ChangeEvent<HTMLInputElement>, rating: number) => void;
  rating?: number;
  trackTrail?: Trail;
  canEdit?: boolean;
};

const TRACK_DETAILS_STRINGS = defineMessages({
  ENDPOINT_CONFIRMATION: {
    defaultMessage: 'Are you sure you want to save changes to this activity? Please note, once saved, these changes cannot be undone.'
  }
});

const TrackDetailsCard = ({
  forcePhotoId,
  track,
  context,
  data,
  blockNonUserCreation,
  handleShareClick,
  messagingChannel,
  isFavorite,
  handleFavoriteClick,
  saveMapAsClone,
  handleTrackUpdate,
  handleRatingChange,
  rating,
  trackTrail,
  canEdit
}: Props): JSX.Element => {
  const {
    formattedDefaultMessages: { ENDPOINT_CONFIRMATION }
  } = useFormatMessage(TRACK_DETAILS_STRINGS);

  const { findByValue } = useActivityStrings();

  const [showEditModal, setShowEditModal] = useState(false);
  const [activityModalEditProps, setActivityModalEditProps] = useState(null);
  const [editingEndpoint, setEditingEndpoint] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const openEditModal = (activityModalProps: any) => {
    setActivityModalEditProps(activityModalProps);
    setShowEditModal(true);
  };

  const getTrackUrl = () =>
    LanguageSupportUtil.wrapUrlSafe(`/explore${track.type === 'map' ? '/map/' : '/recording/'}${track.slug}`, context.languageRegionCode);

  const handleEditClick = () => {
    getEditTrackParams(`${getTrackUrl()}/edit`)
      .then((result: EditTrackResponse) => {
        result.trackParams.activity = findByValue(result.activityUid);

        openEditModal(result);
      })
      .catch(logError);
  };

  const handleDirectionsClick = () => {
    if (!context.currentUser) {
      blockNonUserCreation('explore-atmap-directions');
    }

    const { location } = data;
    window.open(`https://www.google.com/maps/dir/Current+Location/${location?.latitude},${location?.longitude}`);
  };

  const handleShare = () => {
    handleShareClick({
      type: data.presentationType,
      ugc: true,
      id: data.id,
      slug: data.slug,
      name: data.name,
      private: data.private,
      primaryKey: 'id',
      source: ShareSource.Activity
    });
  };

  // admin option
  const handleEditEndpointClick = () => {
    if (editingEndpoint && !confirm(ENDPOINT_CONFIRMATION)) {
      return;
    }
    const newState = { editingEndpoint: !editingEndpoint };
    messagingChannel.publish('map.edit-endpoint-state-changed', newState);
    setEditingEndpoint(!editingEndpoint);
  };
  // admin option
  const handleCancelEditEndpointClick = () => {
    if (!editingEndpoint) {
      return;
    }
    messagingChannel.publish('map.edit-endpoint-state-changed', { editingEndpoint: false, canceled: true });
    setEditingEndpoint(false);
  };

  const toggleDownloadModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const openDownloadModal = () => {
    if (!context.currentUser) {
      const returnToUrl = window.location.pathname + window.location.search;
      modalRoadblock('signup', 'explore-gpx-download', returnToUrl, context.languageRegionCode);
    }

    setShowDownloadModal(true);
  };

  return (
    <div>
      <ActionHeader
        data={data}
        bannerPhotoId={forcePhotoId}
        isFavorite={isFavorite}
        handleFavoriteClick={handleFavoriteClick}
        handleDirectionsClick={handleDirectionsClick}
        handleShareClick={handleShare}
        saveMapAsClone={() => saveMapAsClone(data)}
        editingEndpoint={editingEndpoint}
        handleEditEndpointClick={handleEditEndpointClick}
        handleCancelEditEndpointClick={handleCancelEditEndpointClick}
        handleEditClick={handleEditClick}
        handleDeleteClick={toggleDeleteModal}
        openDownloadModal={openDownloadModal}
        type="track"
        excludeRatingStructuredData
        handleRatingChange={handleRatingChange}
        rating={rating}
        trackTrail={trackTrail}
        canEdit={canEdit}
      />
      {context.currentUser && showDownloadModal && (
        <PortalModal isOpen={context.currentUser && showDownloadModal}>
          <DownloadRouteModal toggleDownloadModal={toggleDownloadModal} mapId={data.id} />
        </PortalModal>
      )}
      {showEditModal && (
        <PortalModal isOpen={showEditModal}>
          <ActivityUploadModal
            {...activityModalEditProps}
            closeModal={() => setShowEditModal(false)}
            user={context.currentUser}
            onTrackAdd={(payload: any) => {
              handleTrackUpdate(payload);
            }}
            disableDropzone
          />
        </PortalModal>
      )}
      <DeleteModal isOpen={showDeleteModal} toggle={toggleDeleteModal} trackId={data.ID} userSlug={data?.user?.slug} />
    </div>
  );
};

export default TrackDetailsCard;
